// cost-cell.ts — #4267, P4 of the #1759 cost-columns plan.
//
// ONE renderer for the three money columns of the home grid — EC2 Cost,
// Token Cost, Total Cost — so there is ONE decision about what an unmeasured
// cost looks like and three readers of it, never three decisions.
//
// DRIVEN at origin/main before this file existed: `ec2_usd` read **0** in
// `helm-console/src/client/home.ts`, with `total_usd` **13**, `tokens_usd`
// **1** and `cost` **77** as the positive controls in the same run. The cell
// did not exist; the grid had one money column named "Total cost" that in
// fact showed the TOKEN figure.
//
// THE THREE STATES A CELL MUST TELL APART, AND THE FOURTH IT MUST NEVER
// RENDER:
//   1. a FIGURE;
//   2. UNMEASURED — with the reason NAMED. #4267 writes this as an em dash;
//      #927 (landed, and measured on production 2026-09-09) forbids a BARE em
//      dash, because the owner watched a release spending $41.84 render one
//      and there was nothing to interrogate. The two rules are not in
//      conflict and this file satisfies both: the CELL carries the dash as
//      `dash: true` and `usd: null` so no arithmetic and no export can read
//      it as a number, and the LABEL is a word that sends the reader to the
//      reason, with the full refusal text in the tip. `EM_DASH` is exported
//      for a caller that wants the glyph itself.
//   3. APPROXIMATE — where P3's CloudWatch egress component is included, the
//      flag is rendered as a mark beside the figure, never folded silently.
//   4. **NEVER `$0.00` FOR UNMEASURED.** A measured zero is a different
//      statement and keeps its `$0.00`; the two are separated by `measured`,
//      never by the figure.
//
// AND THE RULE THE TOTAL EXISTS TO ENFORCE: **Total never sums a `—` as
// zero.** `totalCostCell` composes EC2 and Token, and a component that is
// unmeasured makes the TOTAL unmeasured, naming which half is missing. There
// is no branch here that treats a null component as `0`.

/** The glyph #4267 names. Exported so nothing re-types it, and so a suite can
 * assert BY IDENTITY rather than by eyeballing a dash. */
export const EM_DASH = '—';

/** #1816's LOWER-BOUND marker, restated here (not imported from home.ts) so
 * this module has no dependency on the file it is used by. A SUFFIX: the
 * money columns sort and export as TEXT, so a prefix would sweep every
 * truncated row to one end of the sort. */
export const COST_LOWER_BOUND_SUFFIX = '+';

/** The mark an approximate figure carries. A WORD, not a symbol: a symbol
 * beside a money figure reads as a footnote nobody follows. */
export const COST_APPROXIMATE_MARK = 'approx';

export type MoneyCellState =
  /** A figure the console computed or the server summed. */
  | 'measured'
  /** A MEASURED zero — $0.00 is the answer, not the absence of one. */
  | 'zero'
  /** No figure, and the reason is named. */
  | 'unmeasured';

/** What every money column hands the renderer. Deliberately the shape the
 * plan named: `{ usd | null, approximate, reason, truncated }`, plus the two
 * facts that keep an unmeasured cell honest. */
export interface MoneyCellInput {
  /** null = UNMEASURED. A measured zero is `0` with `measured: true`. */
  usd: number | null;
  measured: boolean;
  /** Named exactly when `usd` is null. An unmeasured cell with no reason is
   * refused by `moneyCell` — a blank that cannot be interrogated is the
   * defect #927 was filed for. */
  reason?: string | null;
  /** The operator action, when there is one. */
  remedy?: string | null;
  /** P3: the figure includes a CloudWatch-derived component. */
  approximate?: boolean;
  approximateNote?: string | null;
  /** #1816 / P2: the figure is known to be SHORT. */
  truncated?: boolean;
  truncatedNote?: string | null;
  /** Who truncated it, when the payload named them (#1844). */
  truncatedIds?: string[];
  /** A short word for the cell when unmeasured — the thing a reader sees
   * before they open the tip. */
  unmeasuredLabel?: string;
  /** Provenance for a measured figure. */
  asOf?: string | null;
  /** The column this cell belongs to, for the tip's first line. */
  columnLabel?: string;
  /** Lane prime-ec2cost2: WHAT a measured figure counts, short form
   * (`instance hours + EBS volume only (lower bound)`), and the long form
   * naming every EXCLUDED component with its reason. A measured figure that
   * carries a basis says it on the tip's second line. */
  basisShort?: string | null;
  basisDetail?: string | null;
}

export interface MoneyCell {
  state: MoneyCellState;
  /** NEVER a bare em dash (#927) and NEVER `$0.00` when unmeasured (#4267).
   * The grid's sort and export value. */
  label: string;
  /** #4267's dash, as a MACHINE-READABLE fact rather than a glyph somebody
   * has to parse out of a label. True exactly when `state === 'unmeasured'`,
   * which is exactly when `usd` is null. */
  dash: boolean;
  /** The glyph itself, for a renderer that wants to paint it beside the
   * word. Empty unless `dash`. */
  dashGlyph: string;
  /** Always non-empty: the reason, or the provenance of the figure. */
  tip: string;
  /** The figure when there is one, else null. NEVER 0 for unmeasured. */
  usd: number | null;
  approximate: boolean;
  truncated: boolean;
  truncatedIds: string[];
  /** The mark painted beside the figure — `approx`, `+`, or both. Empty on a
   * plain measured cell, so a mark on every figure cannot teach people to
   * stop reading marks. */
  mark: string;
  /** Lane prime-ec2cost2: the short basis of a measured figure, '' when the
   * column states none (the token and total columns). Painted as data. */
  basis: string;
}

function fmtUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function marksFor(approximate: boolean, truncated: boolean): string {
  const parts: string[] = [];
  if (truncated) parts.push(COST_LOWER_BOUND_SUFFIX);
  if (approximate) parts.push(COST_APPROXIMATE_MARK);
  return parts.join(' ');
}

/**
 * THE one decision. Pure, total, and it has no branch that returns `$0.00`
 * for an unmeasured input: the only way to `state: 'zero'` is
 * `measured: true` with `usd === 0`.
 */
export function moneyCell(input: MoneyCellInput): MoneyCell {
  const col = input.columnLabel ?? 'This cost';
  const approximate = input.approximate === true;
  const truncated = input.truncated === true;
  const truncatedIds = Array.isArray(input.truncatedIds)
    ? input.truncatedIds.filter((x) => typeof x === 'string')
    : [];

  if (!input.measured || typeof input.usd !== 'number' || !Number.isFinite(input.usd)) {
    // UNMEASURED. The dash rides as data; the label is a word.
    const reason = input.reason && input.reason.trim()
      ? input.reason.trim()
      : 'no reason was recorded, which is itself the defect - a blank cost cell that cannot be '
        + 'interrogated is what #927 was filed for';
    const tip = [
      `${col} is UNMEASURED. It is NOT $0.00 - nothing here says this release cost nothing.`,
      `reason:  ${reason}`,
      input.remedy ? `remedy:  ${input.remedy}` : null,
      approximate && input.approximateNote ? `note:    ${input.approximateNote}` : null,
    ].filter(Boolean).join('\n');
    return {
      state: 'unmeasured',
      label: input.unmeasuredLabel && input.unmeasuredLabel.trim() ? input.unmeasuredLabel.trim() : 'Unmeasured',
      dash: true,
      dashGlyph: EM_DASH,
      tip,
      usd: null,
      approximate,
      truncated,
      truncatedIds,
      mark: '',
      basis: '',
    };
  }

  const mark = marksFor(approximate, truncated);
  const label = `${fmtUsd(input.usd)}${truncated ? COST_LOWER_BOUND_SUFFIX : ''}`;
  const tip = [
    input.usd === 0
      ? `${col}: a MEASURED zero. The console has the facts and they sum to nothing - this is not an absence.`
      : `${col}: ${fmtUsd(input.usd)}.`,
    input.basisShort ? `basis:   ${input.basisShort}` : null,
    input.basisDetail ? input.basisDetail : null,
    input.asOf ? `as of:   ${input.asOf}` : null,
    truncated
      ? `INCOMPLETE - this figure is a LOWER BOUND, not a total.${
          input.truncatedNote ? `\n${input.truncatedNote}` : ''
        }${truncatedIds.length ? `\ntruncated by: ${truncatedIds.join(', ')}` : ''}`
      : null,
    approximate
      ? `APPROXIMATE${input.approximateNote ? ` - ${input.approximateNote}` : ''}`
      : null,
  ].filter(Boolean).join('\n');

  return {
    state: input.usd === 0 ? 'zero' : 'measured',
    label,
    dash: false,
    dashGlyph: '',
    tip,
    usd: input.usd,
    approximate,
    truncated,
    truncatedIds,
    mark,
    basis: input.basisShort ?? '',
  };
}

/* ------------------------------------------------------------------ *
 * The EC2 column.
 * ------------------------------------------------------------------ */

/** The server's `Ec2CostPayload` (src/server/cost-ec2.ts), read defensively.
 * Restated rather than imported: the client bundle does not reach into the
 * server tree, and an unknown spelling is DROPPED, not coerced. */
export interface Ec2CostEntry {
  ec2_usd?: number | null;
  ec2_measured?: boolean;
  ec2_reason?: string | null;
  ec2_remedy?: string | null;
  ec2_refusal_kind?: string | null;
  ec2_approximate?: boolean;
  ec2_approximate_note?: string | null;
  ec2_lower_bound?: boolean;
  ec2_lower_bound_note?: string | null;
  ec2_as_of?: string | null;
  /** The server's `ec2_components` - what the figure COUNTS and what it
   * does not (lane prime-ec2cost2). */
  ec2_components?: Ec2ComponentEntry[] | null;
}

/** One entry of the server's `ec2_components`, read defensively. */
export interface Ec2ComponentEntry {
  key: string;
  usd: number | null;
  measured: boolean;
  reason: string | null;
}

/** Lane prime-ec2cost2 - the plain names the basis line uses, keyed by the
 * server's `Ec2ComponentKey`. An unknown key is shown as itself, never
 * dropped: a component nobody named is still a component. */
export const EC2_COMPONENT_NAMES: Record<string, string> = {
  'instance-hours': 'instance hours',
  ebs: 'EBS volume',
  snapshots: 'EBS snapshots',
  'data-transfer': 'data transfer out',
};

/** What a figure says when the console's answer names NO component: the
 * basis is unknown, so the figure is shown as a LOWER BOUND - a figure
 * without its basis is not shown as complete. */
export const EC2_BASIS_UNKNOWN =
  'basis UNKNOWN: this console`s answer names no cost component for this figure, so what it counts cannot be '
  + 'shown and it is treated as a LOWER BOUND';

/** What a figure says when the answer carries NO component list at all (a
 * console older than the field): the basis is not shown as known. */
export const EC2_BASIS_NOT_REPORTED =
  'basis NOT REPORTED: this console`s answer carries no component list, so what this figure counts cannot be shown';

export interface Ec2Basis {
  /** False when the answer carried no component list at all. */
  known: boolean;
  counted: string[];
  excluded: Array<{ name: string; reason: string | null }>;
  /** e.g. `instance hours + EBS volume only (lower bound)`. */
  short: string;
  /** The tip lines: what is counted, and every EXCLUDED component by name. */
  detail: string;
}

/** PURE. The basis of an EC2 figure, from the server's component list. A
 * component counts only when it is MEASURED with a finite figure; every other
 * component is EXCLUDED and named with its reason - never folded in as $0. */
export function ec2Basis(components: Ec2ComponentEntry[] | null | undefined): Ec2Basis {
  const list = Array.isArray(components)
    ? components.filter((c) => !!c && typeof c.key === 'string')
    : [];
  if (!Array.isArray(components)) {
    return { known: false, counted: [], excluded: [], short: 'basis not reported', detail: EC2_BASIS_NOT_REPORTED };
  }
  if (!list.length) {
    return { known: false, counted: [], excluded: [], short: 'basis unknown (lower bound)', detail: EC2_BASIS_UNKNOWN };
  }
  const nameOf = (k: string) => EC2_COMPONENT_NAMES[k] ?? k;
  const isCounted = (c: Ec2ComponentEntry) =>
    c.measured === true && typeof c.usd === 'number' && Number.isFinite(c.usd);
  const counted = list.filter(isCounted).map((c) => nameOf(c.key));
  const excluded = list.filter((c) => !isCounted(c)).map((c) => ({
    name: nameOf(c.key),
    reason: typeof c.reason === 'string' && c.reason.trim() ? c.reason.trim() : null,
  }));
  const countedText = counted.length ? counted.join(' + ') : 'nothing';
  const short = excluded.length ? `${countedText} only (lower bound)` : countedText;
  const detail = [
    `counts:  ${counted.length ? counted.join(', ') : 'nothing'}`,
    excluded.length
      ? `EXCLUDED - not in this figure, and NOT $0: ${excluded
        .map((e) => (e.reason ? `${e.name} (${e.reason})` : e.name)).join('; ')}`
      : null,
  ].filter(Boolean).join('\n');
  return { known: true, counted, excluded, short, detail };
}

export type Ec2CostMap = Map<string, Ec2CostEntry> | null;

/** The short word an EC2 refusal shows in the cell, by kind. The full
 * sentence is in the tip; this is what fits in a grid column and still sends
 * the reader somewhere. */
export const EC2_UNMEASURED_LABELS: Record<string, string> = {
  'baseline-absent': 'No start stamp',
  'baseline-unreadable': 'Bad start stamp',
  'instance-type-unresolved': 'No instance type',
  'inventory-denied': 'EC2 read denied',
  'inventory-unavailable': 'EC2 read failed',
  'hour-rate-refused': 'No rate',
  'not-wired': 'Not enabled',
};

export function ec2CostCell(releaseId: string | undefined, map: Ec2CostMap): MoneyCell {
  if (!releaseId) {
    return moneyCell({
      usd: null, measured: false, columnLabel: 'EC2 cost',
      unmeasuredLabel: 'Unknown',
      reason: 'no release row was passed to the cell renderer',
    });
  }
  if (!map) {
    return moneyCell({
      usd: null, measured: false, columnLabel: 'EC2 cost',
      unmeasuredLabel: 'Unknown',
      reason: 'the console has not answered GET /api/releases/cost-summary yet',
    });
  }
  const e = map.get(releaseId);
  if (!e) {
    return moneyCell({
      usd: null, measured: false, columnLabel: 'EC2 cost',
      unmeasuredLabel: 'Unmeasured',
      reason:
        'this console`s cost-summary answer carries no EC2 entry for this release - the EC2 lane did not '
        + 'run, or it ran before this release existed',
    });
  }
  const measured = e.ec2_measured === true && typeof e.ec2_usd === 'number' && Number.isFinite(e.ec2_usd);
  // Lane prime-ec2cost2 - NO FIGURE WITHOUT ITS BASIS. The figure names what
  // it counts; one that excludes a component, or whose answer names none, is
  // a LOWER BOUND whatever the payload's own flag says.
  const basis = measured ? ec2Basis(e.ec2_components) : null;
  const basisShort = !basis
    ? null
    : (basis.known && !basis.excluded.length && e.ec2_lower_bound === true)
      ? `${basis.short} (lower bound)`
      : basis.short;
  // An answer that sends the list and names nothing in it is a figure with
  // no basis: LOWER BOUND. An answer with NO list at all (a console older
  // than the field) keeps its own flag; its tip still says the basis is
  // unknown, so the figure is never shown as complete without saying so.
  const basisLower = !!basis
    && (basis.excluded.length > 0 || (!basis.known && Array.isArray(e.ec2_components)));
  return moneyCell({
    usd: measured ? (e.ec2_usd as number) : null,
    measured,
    columnLabel: 'EC2 cost',
    reason: e.ec2_reason ?? null,
    remedy: e.ec2_remedy ?? null,
    approximate: e.ec2_approximate === true,
    approximateNote: e.ec2_approximate_note ?? null,
    truncated: e.ec2_lower_bound === true || basisLower,
    truncatedNote: e.ec2_lower_bound_note ?? (basisLower && basis ? basis.detail : null),
    basisShort,
    basisDetail: basis ? basis.detail : null,
    asOf: e.ec2_as_of ?? null,
    unmeasuredLabel: EC2_UNMEASURED_LABELS[String(e.ec2_refusal_kind ?? '')] ?? 'Unmeasured',
  });
}

/* ------------------------------------------------------------------ *
 * The Total column.
 * ------------------------------------------------------------------ */

/** What the Total needs from each half. Deliberately NOT the whole cell: the
 * total is composed from FIGURES and their measured-ness, so nothing about a
 * label can leak into arithmetic. */
export interface TotalComponent {
  name: string;
  usd: number | null;
  measured: boolean;
  /** Why it is unmeasured — carried into the Total's own reason, so the
   * Total names WHICH half is missing rather than saying "unmeasured". */
  reason: string | null;
  approximate?: boolean;
  truncated?: boolean;
  truncatedIds?: string[];
}

/**
 * **TOTAL NEVER SUMS A `—` AS ZERO.**
 *
 * The rule, stated as code rather than as a comment: a component whose
 * `measured` is false contributes NOTHING to a sum — because there is no sum.
 * One unmeasured component makes the TOTAL unmeasured, and the Total's reason
 * NAMES the components that were missing. There is no `?? 0`, no
 * `Number(x) || 0`, and no filter that silently drops a null and adds up what
 * is left: dropping a null and summing the rest IS summing it as zero, one
 * indirection away, and it is the shape that produced the bug this column
 * exists to prevent.
 *
 * Marks PROPAGATE: a total built from an approximate component is
 * approximate, and a total built from a lower-bound component is a lower
 * bound. Neither can be lost by addition.
 */
export function totalCostCell(components: TotalComponent[]): MoneyCell {
  const missing = components.filter((c) => !c.measured || typeof c.usd !== 'number' || !Number.isFinite(c.usd));
  const approximate = components.some((c) => c.approximate === true);
  const truncated = components.some((c) => c.truncated === true);
  const truncatedIds = [...new Set(components.flatMap((c) => c.truncatedIds ?? []))];

  if (missing.length) {
    return moneyCell({
      usd: null,
      measured: false,
      columnLabel: 'Total cost',
      unmeasuredLabel: missing.length === components.length ? 'Unmeasured' : `Partial (${missing.length} missing)`,
      reason:
        `a total is only a total when every component is measured, and ${missing.length} of `
        + `${components.length} is not: `
        + missing.map((m) => `${m.name} (${m.reason ?? 'no reason recorded'})`).join('; ')
        + ' - the measured components are NOT summed and shown as a total, because a partial sum '
        + 'presented as a total is a smaller number than the truth wearing the truth`s formatting',
      approximate,
      approximateNote: approximate ? 'at least one measured component is approximate' : null,
      truncated,
      truncatedIds,
    });
  }

  const usd = components.reduce((a, c) => a + (c.usd as number), 0);
  return moneyCell({
    usd,
    measured: true,
    columnLabel: 'Total cost',
    approximate,
    approximateNote: approximate
      ? `includes an approximate component: ${components.filter((c) => c.approximate).map((c) => c.name).join(', ')}`
      : null,
    truncated,
    truncatedNote: truncated
      ? `a LOWER BOUND: ${components.filter((c) => c.truncated).map((c) => c.name).join(', ')} is known to be short`
      : null,
    truncatedIds,
  });
}
